import { formatDateTime } from '@batiflow/i18n';
import { SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import { Send20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { AckChip } from './ack-chip';
import { useDiffusion } from './api';
import type { TransmittalDocument } from './model';
import { SendTransmittalDialog } from './send-dialog';
import { useTransmittalsView } from './use-transmittals-view';

const useStyles = __css({"section":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"head":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","Brf1p80":"f1869bpl","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r"},"heading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bg96gwp":"f1i3iumi","Bhrd7zp":"fl43uef","sj55zd":"f19n0e5"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f778t4r"},"entry":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f5j02bv","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"f1dmdbja"},"entryHead":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1j0q4x9"},"number":{"Bhrd7zp":"fl43uef"},"subject":{"Btd35i7":"fjksvth"},"muted":{"sj55zd":"f11d4kpn","Be2twd7":"fy9rknc"},"facts":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Belr9w4":"f1j0q4x9"}});

export interface DiffusionSectionProps {
  spaceId: string;
  document: TransmittalDocument;
  /** The caller manages the space: "Send this document" is offered. */
  canTransmit: boolean;
}

/**
 * The diffusion history of a document, in its panel (GEDX-FE-02): every transmittal that carried
 * one of its versions, with the index sent, the number of recipients and of acknowledgements.
 */
export function DiffusionSection({ spaceId, document, canTransmit }: DiffusionSectionProps) {
  const { t } = useTranslation('transmittals');
  const styles = useStyles();
  const locale = useLocale();
  const view = useTransmittalsView();
  const history = useDiffusion(spaceId, document.id);
  const [sending, setSending] = useState(false);
  const entries = history.data?.items ?? [];

  let body;
  if (history.isError) {
    body = (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>{t('diffusion.loadFailed')}</MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void history.refetch();
            }}
          >
            {t('common:actions.retry')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  } else if (history.isPending) {
    body = <SkeletonList count={2} />;
  } else if (entries.length === 0) {
    body = <p className={styles.muted}>{t('diffusion.empty')}</p>;
  } else {
    body = (
      <ul className={styles.list} aria-label={t('diffusion.listLabel')}>
        {entries.map((entry) => (
          <li key={`${entry.transmittalId}-${entry.versionId}`} className={styles.entry}>
            <div className={styles.entryHead}>
              <bdi dir="ltr" className={styles.number}>
                {entry.numberLabel}
              </bdi>
              <bdi className={styles.subject}>{entry.subject}</bdi>
            </div>
            <div className={styles.facts}>
              <span className={styles.muted}>{isolate(formatDateTime(entry.sentAt, locale))}</span>
              <span className={styles.muted}>
                {entry.revisionIndex
                  ? t('diffusion.sentIndex', { index: isolate(entry.revisionIndex) })
                  : t('documents.noIndex')}
              </span>
              <span className={styles.muted}>
                {t('diffusion.recipients', { count: entry.recipientCount })}
              </span>
              <AckChip acknowledged={entry.acknowledgedCount} total={entry.recipientCount} />
            </div>
            <div>
              <Button
                size="small"
                appearance="subtle"
                onClick={() => {
                  view.show(true, entry.transmittalId);
                }}
              >
                {t('diffusion.open', { number: isolate(entry.numberLabel) })}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section className={styles.section} aria-labelledby="diffusion-title">
      <div className={styles.head}>
        <h3 id="diffusion-title" className={styles.heading}>
          {t('diffusion.title')}
        </h3>
        {canTransmit ? (
          <Button
            size="small"
            icon={<Send20Regular />}
            onClick={() => {
              setSending(true);
            }}
          >
            {t('diffusion.send')}
          </Button>
        ) : null}
      </div>
      {body}
      {sending ? (
        <SendTransmittalDialog
          spaceId={spaceId}
          documents={[document]}
          onClose={() => {
            setSending(false);
          }}
        />
      ) : null}
    </section>
  );
}


import "/opt/batiflow/app/apps/web/src/features/transmittals/diffusion-section.griffel.css";